
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Crown, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import UpgradePrompt from './UpgradePrompt';

interface TrialBannerProps {
  daysRemaining: number;
  isTrialActive: boolean;
  onStartTrial: () => void;
  trialStarted: boolean;
}

const TrialBanner: React.FC<TrialBannerProps> = ({
  daysRemaining,
  isTrialActive,
  onStartTrial,
  trialStarted
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  if (isDismissed) return null;

  const getBannerVariant = () => {
    if (!isTrialActive && trialStarted) return 'expired';
    if (daysRemaining <= 1) return 'urgent';
    if (daysRemaining <= 3) return 'warning';
    return 'active';
  };

  const getBannerStyles = () => {
    const variant = getBannerVariant();
    switch (variant) {
      case 'expired':
        return 'border-red-200 bg-gradient-to-r from-red-50 to-pink-50';
      case 'urgent':
        return 'border-orange-200 bg-gradient-to-r from-orange-50 to-red-50';
      case 'warning':
        return 'border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50';
      default:
        return 'border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50';
    }
  };

  const getBannerMessage = () => {
    if (!trialStarted) {
      return {
        title: "Start Your Free Trial",
        description: "Get 7 days of premium access to all AI-powered tools!"
      };
    }

    if (!isTrialActive) {
      return {
        title: "Trial Expired",
        description: "Your 7-day trial has ended. Upgrade to continue using premium features."
      };
    }

    if (daysRemaining === 0) {
      return {
        title: "Trial Expires Today",
        description: "Your trial ends today. Upgrade now to avoid interruption."
      };
    }

    if (daysRemaining === 1) {
      return {
        title: "Trial Expires Tomorrow",
        description: "Only 1 day left in your trial. Secure your access now!"
      };
    }

    return {
      title: `${daysRemaining} Days Left`,
      description: `Your premium trial expires in ${daysRemaining} days. Upgrade anytime!`
    };
  };

  const message = getBannerMessage();

  return (
    <>
      <AnimatePresence data-id="m8hql9ajt" data-path="src/components/trial/TrialBanner.tsx">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="mb-4" data-id="mevp3eabl" data-path="src/components/trial/TrialBanner.tsx">

          <Card className={`p-4 border-l-4 ${getBannerStyles()}`} data-id="ru4ds78z1" data-path="src/components/trial/TrialBanner.tsx">
            <div className="flex items-center justify-between" data-id="6gjr0l3kd" data-path="src/components/trial/TrialBanner.tsx">
              <div className="flex items-center gap-3" data-id="2ag8wyfaw" data-path="src/components/trial/TrialBanner.tsx">
                <div className="flex items-center gap-2" data-id="sy53bd1uv" data-path="src/components/trial/TrialBanner.tsx">
                  {trialStarted ?
                  <Clock className="h-5 w-5 text-blue-600" data-id="tewtg8n4z" data-path="src/components/trial/TrialBanner.tsx" /> :

                  <Crown className="h-5 w-5 text-purple-600" data-id="2pel6xaem" data-path="src/components/trial/TrialBanner.tsx" />
                  }
                  <div data-id="meth80v8a" data-path="src/components/trial/TrialBanner.tsx">
                    <h4 className="font-semibold text-gray-900" data-id="ivbpzwwcp" data-path="src/components/trial/TrialBanner.tsx">
                      {message.title}
                    </h4>
                    <p className="text-sm text-gray-600" data-id="o10oyotsp" data-path="src/components/trial/TrialBanner.tsx">
                      {message.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2" data-id="v0qx6456y" data-path="src/components/trial/TrialBanner.tsx">
                {!trialStarted ?
                <Button
                  onClick={onStartTrial}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700" data-id="ise1ot8z1" data-path="src/components/trial/TrialBanner.tsx">

                    Start Free Trial
                  </Button> :

                <Button
                  onClick={() => setShowUpgradePrompt(true)}
                  variant={!isTrialActive ? "destructive" : "default"}
                  className={!isTrialActive ? "" : "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"} data-id="b0oj240vs" data-path="src/components/trial/TrialBanner.tsx">

                    <Crown className="h-4 w-4 mr-2" data-id="fu8eo85l4" data-path="src/components/trial/TrialBanner.tsx" />
                    {!isTrialActive ? "Upgrade Required" : "Upgrade Now"}
                  </Button>
                }
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDismissed(true)} data-id="xttdoea54" data-path="src/components/trial/TrialBanner.tsx">

                  <X className="h-4 w-4" data-id="yoxe32tlv" data-path="src/components/trial/TrialBanner.tsx" />
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </AnimatePresence>

      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        trialExpired={!isTrialActive && trialStarted}
        featureName="Premium AI Tools" data-id="yo00mj1o9" data-path="src/components/trial/TrialBanner.tsx" />

    </>);

};

export default TrialBanner;